import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Button,
    Chip,
    Collapse,
    IconButton,
    Typography,
    useMediaQuery,
} from "@mui/material";

import {
    CampaignOutlined,
    Close,
    ContentCopy,
    ExpandLess,
    ExpandMore,
    OpenInNew,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";

const STORAGE_KEY = "ouro.refragPromoDismissed.v1";
const REFRAG_REDEEM_URL = "https://play.refrag.gg/redeem";
const REFRAG_PROMO_CODE = "ouro";
const MOBILE_NAV_MQ = `@media ${MOBILE_NAV_MEDIA}`;
const REFRAG_EXPLAINER_TEXT =
    "Using Refrag to practice for CS2 competitive matches has a lot of advantages. It lets you train in realistic in-game situations, so your aim, movement, crosshair placement, utility usage, and prefire routes all improve in a way that directly transfers to real matches. Refrag is especially useful because it helps you practice specific weaknesses instead of just playing random deathmatch or matchmaking. With tools for warmups, utility lineups, positioning, demo review, and structured training modes, players can build better habits and feel more prepared before going into comp. Overall, Refrag makes practice more focused, efficient, and match-relevant.";

function readDismissed() {
    if (typeof window === "undefined" || !window.localStorage) {
        return false;
    }
    try {
        return window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch (_error) {
        return false;
    }
}

function persistDismissal() {
    if (typeof window === "undefined" || !window.localStorage) {
        return;
    }
    try {
        window.localStorage.setItem(STORAGE_KEY, "1");
    } catch (_error) {
        // localStorage may be unavailable in privacy-restricted contexts.
    }
}

function PromoCopy({
    compact = false,
    showExplainer,
    onToggleExplainer,
    showExplainerToggle = true,
}) {
    return (
        <Box
            sx={{
                minWidth: 0,
                flex: "1 1 18rem",
                [MOBILE_NAV_MQ]: {
                    flex: "0 0 auto",
                    width: "100%",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.55rem",
                    mb: compact ? "0.55rem" : "0.4rem",
                }}
            >
                <Box
                    sx={{
                        width: { xs: "1.7rem", xl: "1.85rem" },
                        height: { xs: "1.7rem", xl: "1.85rem" },
                        borderRadius: "9px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        background: `linear-gradient(
                            165deg,
                            ${alpha(colors.primary.main, 0.22)} 0%,
                            ${alpha(colors.accent.main, 0.14)} 100%
                        )`,
                        border: `1px solid ${alpha(colors.primary.main, 0.28)}`,
                    }}
                >
                    <CampaignOutlined
                        sx={{
                            color: colors.accent.soft,
                            fontSize: compact ? "1rem" : "1.1rem",
                        }}
                    />
                </Box>
                <Typography
                    component="h2"
                    sx={{
                        color: colors.text.primary,
                        fontSize: compact
                            ? { xs: "0.92rem", xl: "0.98rem" }
                            : { xs: "0.95rem", xl: "1.02rem" },
                        fontWeight: 700,
                        letterSpacing: "0.02em",
                        lineHeight: 1.2,
                    }}
                >
                    Refrag partner offer
                </Typography>
            </Box>
            <Typography
                sx={{
                    color: alpha(colors.text.primary, 0.72),
                    fontSize: { xs: "0.8rem", xl: "0.86rem" },
                    lineHeight: 1.55,
                }}
            >
                Get a 3-day trial of Refrag Competitor plus 15% off a Refrag
                subscription. Redeem it at{" "}
                <Box
                    component="a"
                    href={REFRAG_REDEEM_URL}
                    target="_blank"
                    rel="noreferrer noopener"
                    sx={{
                        color: colors.primary.main,
                        textDecoration: "underline",
                        textUnderlineOffset: "2px",
                        "&:hover": { color: colors.accent.hover },
                    }}
                >
                    play.refrag.gg/redeem
                </Box>{" "}
                with code{" "}
                <Box
                    component="strong"
                    sx={{ color: colors.text.primary, fontWeight: 700 }}
                >
                    {REFRAG_PROMO_CODE}
                </Box>
                .
            </Typography>
            {!compact && showExplainerToggle && onToggleExplainer ? (
                <Box
                    sx={{
                        mt: "0.65rem",
                        [MOBILE_NAV_MQ]: { display: "none" },
                    }}
                >
                    <SecondaryLinks
                        showExplainer={Boolean(showExplainer)}
                        onToggleExplainer={onToggleExplainer}
                    />
                </Box>
            ) : null}
        </Box>
    );
}

PromoCopy.propTypes = {
    compact: PropTypes.bool,
    showExplainer: PropTypes.bool,
    onToggleExplainer: PropTypes.func,
    showExplainerToggle: PropTypes.bool,
};

function CopyCodeControl({ codeCopied, onCopy, fullWidth = false }) {
    if (fullWidth) {
        return (
            <Button
                size="small"
                variant="outlined"
                onClick={onCopy}
                startIcon={
                    <ContentCopy
                        sx={{
                            fontSize: "0.95rem !important",
                            color: codeCopied
                                ? colors.state.success.main
                                : alpha(colors.text.primary, 0.55),
                        }}
                    />
                }
                sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    borderRadius: "10px",
                    minHeight: "2.15rem",
                    width: "100%",
                    color: codeCopied
                        ? colors.state.success.main
                        : colors.text.primary,
                    borderColor: codeCopied
                        ? alpha(colors.state.success.main, 0.4)
                        : alpha("#fff", 0.12),
                    backgroundColor: codeCopied
                        ? alpha(colors.state.success.main, 0.1)
                        : alpha("#fff", 0.04),
                    "&:hover": {
                        borderColor: codeCopied
                            ? alpha(colors.state.success.main, 0.55)
                            : alpha("#fff", 0.2),
                        backgroundColor: codeCopied
                            ? alpha(colors.state.success.main, 0.14)
                            : alpha("#fff", 0.07),
                    },
                }}
            >
                {codeCopied ? "Copied" : `Code: ${REFRAG_PROMO_CODE}`}
            </Button>
        );
    }

    return (
        <Chip
            size="small"
            clickable
            onClick={onCopy}
            icon={
                <ContentCopy
                    sx={{
                        fontSize: "0.9rem !important",
                        color: `${
                            codeCopied
                                ? colors.state.success.main
                                : alpha(colors.text.primary, 0.55)
                        } !important`,
                    }}
                />
            }
            label={codeCopied ? "Copied" : `Code: ${REFRAG_PROMO_CODE}`}
            sx={{
                height: "1.85rem",
                backgroundColor: codeCopied
                    ? alpha(colors.state.success.main, 0.1)
                    : alpha("#fff", 0.05),
                border: `1px solid ${
                    codeCopied
                        ? alpha(colors.state.success.main, 0.4)
                        : alpha("#fff", 0.08)
                }`,
                color: codeCopied
                    ? colors.state.success.main
                    : colors.text.primary,
                fontWeight: 700,
                letterSpacing: "0.04em",
                "& .MuiChip-label": { px: "0.55rem" },
                "&:hover": {
                    backgroundColor: codeCopied
                        ? alpha(colors.state.success.main, 0.14)
                        : alpha("#fff", 0.08),
                },
            }}
        />
    );
}

CopyCodeControl.propTypes = {
    codeCopied: PropTypes.bool.isRequired,
    onCopy: PropTypes.func.isRequired,
    fullWidth: PropTypes.bool,
};

function RedeemButton({ fullWidth = false }) {
    return (
        <Button
            href={REFRAG_REDEEM_URL}
            target="_blank"
            rel="noreferrer noopener"
            size="small"
            variant="contained"
            endIcon={<OpenInNew sx={{ fontSize: "0.95rem !important" }} />}
            sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                px: "1.1rem",
                minHeight: "2.15rem",
                width: fullWidth ? "100%" : "auto",
                color: colors.text.contrast,
                background: `linear-gradient(
                    180deg,
                    ${colors.accent.main} 0%,
                    ${colors.primary.main} 100%
                )`,
                boxShadow: `0 8px 20px ${alpha(colors.primary.main, 0.22)}`,
                "&:hover": {
                    background: `linear-gradient(
                        180deg,
                        ${colors.accent.hover} 0%,
                        ${colors.primary.hover} 100%
                    )`,
                    boxShadow: `0 10px 24px ${alpha(colors.primary.main, 0.28)}`,
                },
            }}
        >
            Redeem on Refrag
        </Button>
    );
}

RedeemButton.propTypes = {
    fullWidth: PropTypes.bool,
};

function SecondaryLinks({
    showExplainer,
    dismissible,
    onToggleExplainer,
    onDismiss,
}) {
    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "0.85rem",
            }}
        >
            <Button
                size="small"
                onClick={onToggleExplainer}
                endIcon={showExplainer ? <ExpandLess /> : <ExpandMore />}
                sx={{
                    px: 0,
                    minWidth: 0,
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.8rem",
                    color: alpha(colors.text.primary, 0.55),
                    "&:hover": {
                        backgroundColor: "transparent",
                        color: colors.primary.main,
                    },
                }}
            >
                What is refrag
            </Button>
            {dismissible ? (
                <Button
                    size="small"
                    onClick={onDismiss}
                    sx={{
                        px: 0,
                        minWidth: 0,
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "0.8rem",
                        color: alpha(colors.text.primary, 0.45),
                        "&:hover": {
                            backgroundColor: "transparent",
                            color: colors.text.primary,
                        },
                    }}
                >
                    Dismiss
                </Button>
            ) : null}
        </Box>
    );
}

SecondaryLinks.propTypes = {
    showExplainer: PropTypes.bool.isRequired,
    dismissible: PropTypes.bool,
    onToggleExplainer: PropTypes.func.isRequired,
    onDismiss: PropTypes.func,
};

function PromoActions({
    compact,
    codeCopied,
    showExplainer,
    dismissible,
    onCopy,
    onToggleExplainer,
    onDismiss,
}) {
    const isMobile = useMediaQuery(MOBILE_NAV_MEDIA);

    if (compact) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: "0.75rem",
                }}
            >
                <RedeemButton fullWidth />
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: "0.75rem",
                    }}
                >
                    <CopyCodeControl codeCopied={codeCopied} onCopy={onCopy} />
                    <SecondaryLinks
                        showExplainer={showExplainer}
                        dismissible={dismissible}
                        onToggleExplainer={onToggleExplainer}
                        onDismiss={onDismiss}
                    />
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: isMobile ? "stretch" : "flex-end",
                gap: isMobile ? "0.55rem" : "0.65rem",
                flexShrink: 0,
                width: isMobile ? "100%" : "auto",
                minWidth: isMobile ? 0 : { sm: "11.5rem" },
            }}
        >
            <RedeemButton fullWidth={isMobile} />
            <CopyCodeControl
                codeCopied={codeCopied}
                onCopy={onCopy}
                fullWidth={isMobile}
            />
        </Box>
    );
}

PromoActions.propTypes = {
    compact: PropTypes.bool,
    codeCopied: PropTypes.bool.isRequired,
    showExplainer: PropTypes.bool.isRequired,
    dismissible: PropTypes.bool,
    onCopy: PropTypes.func.isRequired,
    onToggleExplainer: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired,
};

export default function RefragPromoNotice({
    variant = "banner",
    dismissible = false,
}) {
    const [dismissed, setDismissed] = useState(
        () => dismissible && readDismissed(),
    );
    const [showExplainer, setShowExplainer] = useState(false);
    const [codeCopied, setCodeCopied] = useState(false);

    const dismissPromo = useCallback(() => {
        persistDismissal();
        setDismissed(true);
    }, []);

    const copyPromoCode = useCallback(async () => {
        try {
            if (typeof navigator?.clipboard?.writeText !== "function") {
                return;
            }
            await navigator.clipboard.writeText(REFRAG_PROMO_CODE);
            setCodeCopied(true);
            window.setTimeout(() => setCodeCopied(false), 1600);
        } catch (_error) {
            setCodeCopied(false);
        }
    }, []);

    if (dismissible && dismissed) {
        return null;
    }

    const shellSx = {
        position: "relative",
        overflow: "hidden",
        borderRadius: "14px",
        border: `1px solid ${alpha(colors.primary.main, 0.22)}`,
        background: `
            linear-gradient(
                165deg,
                ${alpha(colors.background.card, 0.94)} 0%,
                ${alpha(colors.background.surface, 0.9)} 48%,
                ${alpha(colors.background.dark, 0.94)} 100%
            )
        `,
        boxShadow: `
            0 12px 36px rgba(0, 0, 0, 0.38),
            inset 0 1px 0 ${alpha("#fff", 0.04)}
        `,
        "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background: `
                radial-gradient(
                    ellipse 70% 90% at 0% 0%,
                    ${alpha(colors.primary.main, 0.12)},
                    transparent 55%
                )
            `,
        },
    };

    if (variant === "card") {
        return (
            <Box sx={{ ...shellSx, p: { xs: "1rem 1.1rem", xl: "1.15rem 1.25rem" } }}>
                <Box sx={{ position: "relative", zIndex: 1 }}>
                    <PromoCopy compact />
                    <Box sx={{ mt: "0.9rem" }}>
                        <PromoActions
                            compact
                            codeCopied={codeCopied}
                            showExplainer={showExplainer}
                            dismissible={dismissible}
                            onCopy={copyPromoCode}
                            onToggleExplainer={() =>
                                setShowExplainer((prev) => !prev)
                            }
                            onDismiss={dismissPromo}
                        />
                    </Box>
                    <Collapse in={showExplainer}>
                        <Typography
                            sx={{
                                mt: "0.85rem",
                                color: alpha(colors.text.primary, 0.68),
                                fontSize: { xs: "0.8rem", xl: "0.84rem" },
                                lineHeight: 1.65,
                            }}
                        >
                            {REFRAG_EXPLAINER_TEXT}
                        </Typography>
                    </Collapse>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                ...shellSx,
                width: "100%",
                mb: { xs: "1rem", xl: "1.15rem" },
                mt: { xs: "0.25rem", xl: "0.35rem" },
                p: {
                    xs: "0.9rem 1rem",
                    xl: "1rem 1.25rem",
                },
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    alignItems: { xs: "flex-start", md: "center" },
                    justifyContent: "space-between",
                    gap: { xs: "0.85rem", md: "1.25rem" },
                    pr: dismissible ? { xs: 0, sm: "1.75rem" } : 0,
                    [MOBILE_NAV_MQ]: {
                        flexDirection: "column",
                        flexWrap: "nowrap",
                        alignItems: "stretch",
                        justifyContent: "flex-start",
                        gap: "0.85rem",
                    },
                }}
            >
                <PromoCopy
                    showExplainer={showExplainer}
                    onToggleExplainer={() =>
                        setShowExplainer((prev) => !prev)
                    }
                />
                <PromoActions
                    codeCopied={codeCopied}
                    showExplainer={showExplainer}
                    dismissible={false}
                    onCopy={copyPromoCode}
                    onToggleExplainer={() =>
                        setShowExplainer((prev) => !prev)
                    }
                    onDismiss={dismissPromo}
                />
                <Box
                    sx={{
                        display: "none",
                        [MOBILE_NAV_MQ]: { display: "block" },
                    }}
                >
                    <SecondaryLinks
                        showExplainer={showExplainer}
                        onToggleExplainer={() =>
                            setShowExplainer((prev) => !prev)
                        }
                    />
                </Box>
                {dismissible ? (
                    <IconButton
                        aria-label="Dismiss Refrag promo"
                        size="small"
                        onClick={dismissPromo}
                        sx={{
                            position: "absolute",
                            top: "-0.2rem",
                            right: "-0.35rem",
                            color: alpha(colors.text.primary, 0.4),
                            "&:hover": {
                                color: colors.text.primary,
                                backgroundColor: alpha("#fff", 0.06),
                            },
                        }}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                ) : null}
            </Box>
            <Collapse in={showExplainer}>
                <Typography
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        mt: "0.85rem",
                        color: alpha(colors.text.primary, 0.68),
                        fontSize: { xs: "0.8rem", xl: "0.84rem" },
                        lineHeight: 1.65,
                        maxWidth: "58rem",
                    }}
                >
                    {REFRAG_EXPLAINER_TEXT}
                </Typography>
            </Collapse>
        </Box>
    );
}

RefragPromoNotice.propTypes = {
    dismissible: PropTypes.bool,
    variant: PropTypes.oneOf(["banner", "card"]),
};
